import React, { FC, memo, useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { format } from 'date-fns';

import { gatewayService } from 'services';
import { Spinner } from 'components/atoms';
import { AdminPageProps } from 'components/AppRouter';
import { Client } from 'types/Client';
import { Customer } from 'types/Customer';
import { NetworkError } from 'types/Error';
import { Period } from 'types/Generic';
import { ITableHeader, CellType } from 'types/Header';
import { FieldType } from 'types/Service';
import {
  DATE_DROPDOWN_OPTIONS,
  ISO_FORMAT,
  CURRENCY_NUMBER_DROPDOWN_OPTIONS
} from '../../../../../constants';
import { theme } from 'theme';
import {
  Card,
  AdminFormLine,
} from 'theme/mixins';
import {
  getNetworkErrors,
  formatCurrency,
  createDropdownOption
} from 'utils/general';

import { useSortFilterSearch } from '../../hooks';
import type { SortFilterSearchState } from '../../hooks/useSortFilterSearch';
import { PageHeader, Table } from '../../components';

import {
  Wrapper,
} from './Customers.styles';

interface State {
  customersFilterLoaded: boolean;
  client: {
    loading: boolean;
    data: Client | null;
    error: NetworkError | null;
  };
  customers: {
    loading: boolean;
    data: Customer[] | null;
    total: number | null;
    fields: any;
    offset: number;
    error: NetworkError | null;
  };
}

const initialState: State = {
  customersFilterLoaded: false,
  client: {
    loading: false,
    data: null,
    error: null
  },
  customers: {
    loading: false,
    total: null,
    data: null,
    fields: {},
    offset: 0,
    error: null
  }
};

const Customers: FC<AdminPageProps> = props => {
  const {
    addToast,
    sideNavOpen,
    userData
  } = props;

  const navigate = useNavigate();
  const [state, setState] = useState<State>(initialState);
  const { clientId } = useParams();

  const filterStateCache = useRef<SortFilterSearchState | null>(null);
  const prevFilterStateCache = useRef<SortFilterSearchState | null>(null);

  const {
    renderFilterControl: customerFilterControlMarkup,
    renderPaginationAndSort: customerPaginationMarkup,
    renderAppliedFilters,
    renderSearch: customerSearchMarkup,
    filterApplied,
    filterState,
    sortState: customerSortState,
    setSortState: customerSetSortState,
    setInitialFilterState
  } = useSortFilterSearch({
    listItemId: 'Customer',
    total: state.customers.total,
    offset: state.customers.offset,
    filterOptions: {
      fields: [
        {
          label: 'Created',
          name: 'created.at',
          type: FieldType.DateTime,
          options: [
            createDropdownOption('Today', Period.Today),
            createDropdownOption('Yesterday', Period.Yesterday),
            createDropdownOption('This week', Period.ThisWeek),
            createDropdownOption('Last week', Period.LastWeek),
            createDropdownOption('This month', Period.ThisMonth),
            createDropdownOption('Last month', Period.LastMonth),
            createDropdownOption('This quarter', Period.ThisQuarter),
            createDropdownOption('Last quarter', Period.LastQuarter),
            createDropdownOption('This year', Period.ThisYear),
            createDropdownOption('Last year', Period.LastYear),
            ...DATE_DROPDOWN_OPTIONS
          ],
          default: {
            'created.at#datetime': `today=${format(new Date(), ISO_FORMAT)}`
          }
        },
        {
          label: 'Balance',
          name: 'balance',
          type: FieldType.Currency,
          options: [ ...CURRENCY_NUMBER_DROPDOWN_OPTIONS ],
          default: {
            'balance#currency': 'less-than=0'
          }
        },
        {
          label: 'Bookings',
          name: 'bookings',
          type: FieldType.Number,
          options: [ ...CURRENCY_NUMBER_DROPDOWN_OPTIONS ],
          default: {
            'bookings#number': 'less-than=0'
          }
        },
      ]
    },
    sortOptions: {
      options: [
        createDropdownOption('First name', 'firstName'),
        createDropdownOption('Company name', 'businessName'),
        createDropdownOption('Email', 'email'),
        createDropdownOption('Balance', 'balance'),
        createDropdownOption('Bookings', 'bookings')
      ],
      defaultSorting: {
        key: 'created.at',
        label: 'Created',
        direction: -1
      }
    },
    onPrev: (sfsState) => fetchCustomers(sfsState),
    onNext: (sfsState) => fetchCustomers(sfsState),
    onReset: (sfsState) => fetchCustomers(sfsState),
    onFilter: (sfsState) => fetchCustomers(sfsState),
    onSort: (sfsState) => fetchCustomers(sfsState),
    onSave: (sfsState) => saveCustomersFilter(sfsState),
    onSearch: (sfsState) => fetchCustomers(sfsState)
  });

  filterStateCache.current = filterState;

  const fetchCustomers = useCallback((sfsState?: SortFilterSearchState) => {
    if (state.customers.loading) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      customers: {
        ...prevState.customers,
        loading: true
      }
    }));

    gatewayService.getCustomers(clientId!, sfsState)
      .then((customersResponse: any) => {
        setState(prevState => ({
          ...prevState,
          customers: {
            loading: false,
            total: customersResponse.total,
            data: [
              ...JSON.parse(JSON.stringify(customersResponse.data))
            ],
            fields: customersResponse.fields,
            offset: customersResponse.offset,
            error: null
          }
        }));
      })
      .catch((err) => {
        const error: NetworkError = getNetworkErrors([err])[0];

        setState(prevState => ({
          ...prevState,
          customers: {
            ...prevState.customers,
            loading: false,
            error
          }
        }));

        addToast({
          type: 'error',
          content: error.message
        });
      });
  }, [
    state.customers,
    clientId,
    addToast
  ]);

  const loadCustomersFilter = useCallback(() => {
    gatewayService.getUser(clientId!, userData.user!._id)
      .then((userResponse) => {
        if (userResponse.data.customersFilter) {
          setInitialFilterState(userResponse.data.customersFilter);
        }

        setState(prevState => ({
          ...prevState,
          customersFilterLoaded: true
        }));
      })
      .catch((err) => {
        const error: NetworkError = getNetworkErrors([err])[0];

        console.log('---customers filter load error', error.message);
      });
  }, [
    clientId,
    userData.user,
    setInitialFilterState
  ]);

  const saveCustomersFilter = useCallback((sfsState: SortFilterSearchState) => {
    if (JSON.stringify(sfsState) === JSON.stringify(prevFilterStateCache.current)) {
      return;
    }

    gatewayService.updateUser(
      clientId!,
      userData.user!._id,
      {
        customersFilter: sfsState
      }
    )
      .then((updateUserResponse) => {
        prevFilterStateCache.current = updateUserResponse.data.jobsFilter;
      })
      .catch((err) => {
        const error: NetworkError = getNetworkErrors([err])[0];

        console.log('---customers filter save error', error.message);
      });

    prevFilterStateCache.current = sfsState;
  }, [
    clientId,
    userData.user
  ]);

  const getCustomerHeaderConfig = useCallback((): ITableHeader[] => {
    return [
      {
        key: 'firstName',
        label: 'Name',
        width: '33.33%',
        type: CellType.Text,
        sortable: true,
        editable: true,
        format: (_, row) => `${row.firstName} ${row.lastName}`
      },
      {
        key: 'businessName',
        label: 'Company name',
        width: '33.33%',
        type: CellType.Text,
        sortable: true,
      },
      {
        key: 'email',
        label: 'Email',
        width: '43.33%',
        type: CellType.Text,
        sortable: true,
      },
      {
        key: 'phone',
        label: 'Phone',
        width: '33.33%',
        type: CellType.Text,
      },
      {
        key: 'balance',
        label: 'Balance',
        width: '23.33%',
        sortable: true,
        type: CellType.Currency,
        format: (value: number) => formatCurrency(value)
      },
      {
        key: 'bookings',
        label: 'Bookings',
        width: '15.33%',
        sortable: true,
        type: CellType.Number
      }
    ];
  }, []);

  const onRowClick = useCallback((row: any) => {
    navigate({ pathname: `/${clientId}/back-office/customers/${row._id}` });
  }, [
    clientId,
    navigate
  ]);

  const renderCustomers = useCallback(() => {
    if (state.customers.loading) {
      return (
        <Spinner
          color={theme.textColor}
          size={'M'}
        />
      );
    }

    // TODO: show error

    if (!state.customers.data) {
      return null;
    }

    return (
      <Table
        namespaceKey={'customers'}
        loading={state.customers.loading}
        headerConfig={getCustomerHeaderConfig()}
        rows={state.customers.data! || []}
        total={state.customers.total!}
        offset={state.customers.offset}
        footerContent={customerPaginationMarkup(true)}
        sortState={customerSortState}
        onRowClick={onRowClick}
        onSort={customerSetSortState}
      />
    );
  }, [
    state.customers,
    customerSortState,
    customerSetSortState,
    customerPaginationMarkup,
    getCustomerHeaderConfig,
    onRowClick,
  ]);

  useEffect(() => {
    if (!state.customers.data && !state.customers.error) {
      if (state.customersFilterLoaded) {
        fetchCustomers(filterState);
      }
    }
  }, [
    filterState,
    state.customers.data,
    state.customers.error,
    state.customersFilterLoaded,
    fetchCustomers
  ]);

  useEffect(() => loadCustomersFilter(), [loadCustomersFilter]);

  return (
    <Wrapper>
      <PageHeader
        title={'Customers'}
      />
      <AdminFormLine>
        <Card>
          <AdminFormLine marginBottom spaceBetween>
            {customerSearchMarkup('Search customers')}
            {customerFilterControlMarkup(sideNavOpen, true)}
          </AdminFormLine>
          {filterApplied && (
            <AdminFormLine marginBottom>
              {renderAppliedFilters()}
            </AdminFormLine>
          )}
          <AdminFormLine marginBottom>
            {state.customers.data && customerPaginationMarkup()}
          </AdminFormLine>
          {renderCustomers()}
        </Card>
      </AdminFormLine>
    </Wrapper>
  );
};

export default memo(Customers);

